/** @jsxFrag React.Fragment */
import * as React from 'react';

import { FormattedHTMLMessage, FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link } from '@coursera/cds-core';

import { LEARNER_HELP_CENTER_LINK } from 'bundles/authentication/constants';
import {
  USER_NAME_MAX_LENGTH,
  USER_NAME_MIN_LENGTH,
  USER_PASSWORD_MAX_LENGTH,
  USER_PASSWORD_MIN_LENGTH,
} from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/user-account';

export const allMessages = {
  get accountIsAlreadyLinked() {
    return (
      <FormattedHTMLMessage
        message={_t(
          "Your account is already linked to another social login account. Please log in using your prior login method and 'unlink' in your account settings. If the problem persists, visit the {learnerHelpCenterLink}."
        )}
        learnerHelpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>Learner Help Center</a>}
      />
    );
  },
  get accountLinkedToSocialAccount() {
    return _t('The Coursera account is linked to a Social account');
  },
  get twoFactorInvalid() {
    return _t('Wrong two-factor verification code or password. Please try again!');
  },
  get twoFactorRequired() {
    return _t(`Please enter your two-factor verification code generated by your mobile app.`);
  },
  get geoRestriction() {
    return _t('Access from your country/region is restricted.');
  },
  get invalidCredential() {
    return _t('Wrong email or password. Please try again!');
  },
  get invalidWebToken() {
    return _t('Your invitation is invalid.');
  },
  get malformedName() {
    return _t(
      'Please enter your full name. It should be between #{minLength} and #{maxLength} characters. It should not be an email address.',
      {
        minLength: USER_NAME_MIN_LENGTH,
        maxLength: USER_NAME_MAX_LENGTH,
      }
    );
  },
  get forbiddenName() {
    return _t(
      'Invalid name. Please use only letters, spaces, hyphens, and apostrophes, and avoid authoritative titles.'
    );
  },
  get existingCourseraAccount() {
    return _t('A coursera.org account with that email address already exists.');
  },
  get malformedEmailAddress() {
    return _t('The email address you entered is not valid.');
  },
  get failedPasswordRequirements() {
    const locale = _t.getLocale();

    return (
      <>
        <div>{_t('There seems to be an issue with the password you entered. Please keep in mind the following:')}</div>
        <br />
        <ul>
          <li>
            {_t('Password must be between #{minLength} and #{maxLength} characters.', {
              minLength: USER_PASSWORD_MIN_LENGTH,
              maxLength: USER_PASSWORD_MAX_LENGTH,
            })}
          </li>
          <li>
            {_t(
              "Ensure your password is sufficiently complex. Avoid simple passwords like 'qwerty123' or 'password123'."
            )}
          </li>
        </ul>
        <FormattedMessage
          message={_t('Please try again, if problem persists visit the {helpCenterLink}.')}
          helpCenterLink={<Link href={`https://learner.coursera.help/hc/${locale}`}>{_t('Learner Help Center')}</Link>}
        />
      </>
    );
  },
  get passwordDoesntMatch() {
    return _t("Password doesn't match");
  },
  get invalidNewPassword() {
    return _t('Your new password cannot be the same as your old password.');
  },
  get invalidPassword() {
    return _t('Wrong password. Please try again!');
  },
  get incorrectPasswrd() {
    return _t('Password is not correct');
  },
  get tooManyRequests() {
    return _t("You've made too many login attempts. Please try again in a few minutes.");
  },
  get emailAlreadyInUse() {
    return _t("Someone's already using this email.");
  },

  get updateSucceeded() {
    return _t('Update successful!');
  },
  get updateFailed() {
    return _t('Update failed. Please try again.');
  },
  get updateFailedFullName() {
    return _t('Please provide a name');
  },

  get recaptchaFailed() {
    return (
      <FormattedHTMLMessage
        message={_t(
          'There was a reCAPTCHA failure, which means you either failed the challenge or there was an issue loading reCAPTCHA. Please try again and visit <a href="https://support.google.com/recaptcha">Google\'s reCAPTCHA support page</a> for more troubleshooting tips.'
        )}
      />
    );
  },

  get unknownError() {
    return (
      <FormattedHTMLMessage
        message={_t(
          'Something went wrong. Please refresh the page and try again. If the problem persists, visit the {learnerHelpCenterLink}'
        )}
        learnerHelpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>Learner Help Center</a>}
      />
    );
  },
};

/**
 * Extracts the error code from an API response.
 *
 * This function handles responses that return either an error code or a string describing the error.
 * It maps known error messages to predefined error codes.
 * If the response is an object, it returns the `errorCode` property if available.
 * If the response is a string, it maps the string to a corresponding error code.
 * If no known error code or message is found, it returns 'unknownError'.
 *
 * @param {Object|string} response - The API response containing either an error code or an error message.
 * @param {string} [response.errorCode] - The error code from the API response, if available.
 * @returns {string} The extracted or mapped error code.
 */
export const getResponseErrorCode = (response: { errorCode?: string } | string): string => {
  // this mapping is necessary for APIs that currently return a string describing the error, but not an error code
  if (typeof response === 'string') {
    const responseToErrorCode = {
      'Account already exists': 'existingCourseraAccount',
      'Password was not complex enough': 'failedPasswordRequirements',
      'Email is already used by another account.': 'emailAlreadyInUse',
      'This email is not valid.': 'invalidEmail',
      'Incorrect password': 'incorrectPassword',
    };

    // @ts-expect-error TS7016 TODO: responseToErrorCode[response] needs to be typed
    return responseToErrorCode[response] || 'unknownError';
  } else {
    const { errorCode } = response;

    return errorCode || 'unknownError';
  }
};

/**
 * @deprecated This function is only used within the account-settings bundle. Please move the function to that bundle or deprecate it completely.
 */
export function getClientSideMessages() {
  return {
    NAME_VALIDATION: _t('Name must be between #{minLength} and #{maxLength} characters', {
      minLength: USER_NAME_MIN_LENGTH,
      maxLength: USER_NAME_MAX_LENGTH,
    }),
    PASSWORD_LEN_VALIDATION: _t('Password must be between #{minLength} and #{maxLength} characters', {
      minLength: USER_PASSWORD_MIN_LENGTH,
      maxLength: USER_PASSWORD_MAX_LENGTH,
    }),
    EMAIL_VALIDATION: _t('Email must have a "@" followed by a "." symbol. For example: "learner@coursera.org"'),
    EMAIL_GMAIL_SPELLING: _t('Did you mean "@gmail.com"?'),
  };
}

/** Mapping an error code returned from the BE to a message we will render on the FE,
 * see https://github.com/webedx-spark/infra-services/blob/main/services/authentication/app/org/coursera/authentication/thirdpartyauth/AuthProvider.scala#L272-L304
 */
export const getErrorMessages = {
  // login
  get accountIsAlreadyLinked() {
    return allMessages.accountIsAlreadyLinked;
  },
  get twoFactorInvalid() {
    return allMessages.twoFactorInvalid;
  },
  get twoFactorRequired() {
    return allMessages.twoFactorRequired;
  },
  get geoRestriction() {
    return allMessages.geoRestriction;
  },
  get invalidCredential() {
    return allMessages.invalidCredential;
  },
  get invalidWebToken() {
    return allMessages.invalidWebToken;
  },

  // signup
  get invalidName() {
    return allMessages.malformedName;
  },
  get existingCourseraAccount() {
    return allMessages.existingCourseraAccount;
  },
  get malformedEmailAddress() {
    return allMessages.malformedEmailAddress;
  },
  get failedPasswordRequirements() {
    return allMessages.failedPasswordRequirements;
  },

  // complete
  get accountLinkedToSocialAccount() {
    return allMessages.accountLinkedToSocialAccount;
  },
  get invalidPassword() {
    return allMessages.invalidPassword;
  },

  // rate-limited
  get tooManyRequests() {
    return allMessages.tooManyRequests;
  },

  // account settings
  get invalidEmail() {
    return allMessages.malformedEmailAddress;
  },
  get emailAlreadyInUse() {
    return allMessages.emailAlreadyInUse;
  },
  get incorrectPassword() {
    return allMessages.invalidPassword;
  },
  get recaptchaFailed() {
    return allMessages.recaptchaFailed;
  },

  get unknownStatus() {
    return allMessages.unknownError;
  },
  get unknownError() {
    return allMessages.unknownError;
  },
};

/**
 * @deprecated This function is only used within the account-settings bundle. Please move the type to that bundle.
 *
 * Mapping a status in the Settings store to a message we will render on the FE
 */
export const getStatusMessages = {
  get UPDATE_SUCCEEDED() {
    return allMessages.updateSucceeded;
  },
  get UPDATE_FAILED() {
    return allMessages.updateFailed;
  },
  get UPDATE_FAILED_FULL_NAME() {
    return allMessages.updateFailedFullName;
  },
  get UPDATE_FAILED_EMAIL_ADDRESS() {
    return allMessages.malformedEmailAddress;
  },
  get UPDATE_FAILED_NAME_MALFORMED() {
    return allMessages.malformedName;
  },
  get UPDATE_FAILED_NAME_FORBIDDEN() {
    return allMessages.forbiddenName;
  },
  get UPDATE_FAILED_EMAIL_IN_USE() {
    return allMessages.emailAlreadyInUse;
  },
  get UPDATE_FAILED_NEW_PASSWORD() {
    return allMessages.passwordDoesntMatch;
  },
  get UPDATE_FAILED_PASSWORD() {
    return allMessages.incorrectPasswrd;
  },
  get FAILED_NEW_PASSWORD_NOT_SUFFICIENTLY_COMPLEX() {
    return allMessages.failedPasswordRequirements;
  },
  get FAILED_SAME_PASSWORD() {
    return allMessages.invalidNewPassword;
  },
};
